import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { RunnerActionsDropdown } from "@/components/runners/RunnerActionsDropdown";
import { RunnerCreatedAt } from "@/components/runners/RunnerCreatedAt";
import { RunnerCreatedBy } from "@/components/runners/RunnerCreatedBy";
import { RunnerIcon } from "@/components/runners/RunnerIcon";
import { RunnerName } from "@/components/runners/RunnerName";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { RunnerProviderLabel } from "@/components/runners/RunnerProvider";
import { Tooltip } from "@/components/Tooltip";
import type { PlainRunner } from "@/queries/runner-queries";
import { RunnerKind, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const RunnerCard: FC<{ runner: PlainRunner; integrations: SCMIntegration[] }> = ({ runner, integrations }) => {
    const navigate = useNavigate();
    const isSelectable = runner.kind === RunnerKind.REMOTE;

    const isNoRepoAccessConfigured = integrations && integrations.length === 0;
    const isConnected = runner.status?.phase === RunnerPhase.ACTIVE || runner.status?.phase === RunnerPhase.DEGRADED;

    const handleOnClick = useCallback(() => {
        if (!isSelectable) {
            return;
        }
        navigate(`/settings/runners/${runner.runnerId}`);
    }, [isSelectable, navigate, runner.runnerId]);

    return (
        <div
            data-testid={"runner-" + runner.runnerId}
            onClick={handleOnClick}
            className={cn(
                "flex w-[330px] flex-col gap-2 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-4 text-left text-xs",
                isSelectable
                    ? "hover:outline hover:outline-1 hover:outline-border-yield focus-visible:ring-1"
                    : "cursor-default",
            )}
            aria-label="View Details"
        >
            <div className="flex grow">
                <div className="flex grow flex-col gap-0.5">
                    <div className="flex flex-row">
                        <div className="grow">
                            <RunnerIcon runner={runner} />
                        </div>
                        <div>
                            <RunnerActionsDropdown runner={runner} showViewDetails={true} />
                        </div>
                    </div>
                    <RunnerName name={runner.name} className="w-48" />
                    <RunnerProviderLabel provider={runner.provider} />
                </div>
            </div>
            <div className="flex flex-row gap-2">
                <RunnerPhaseTag runner={runner} />
                {isNoRepoAccessConfigured && isConnected && <NoRepoAccessConfigured />}
            </div>
            <div className="flex flex-col">
                <RunnerCreatedAt runner={runner} />
                <RunnerCreatedBy runner={runner} />
            </div>
        </div>
    );
};

export const AddNewRunnerCard: FC<{ setShowNewRunnerModal: (show: boolean) => void }> = ({ setShowNewRunnerModal }) => {
    return (
        <div data-testid="add-new-runner-card" className="min-h-40 w-[330px] rounded-lg text-content-secondary">
            <Button
                variant="ghost"
                className="flex h-full w-full flex-col gap-2 border-0.5 border-border-light bg-surface-primary hover:bg-surface-tertiary"
                onClick={() => setShowNewRunnerModal(true)}
            >
                <IconPlus size="base" />
                <div className="text-xs">Set up a new runner</div>
            </Button>
        </div>
    );
};

const NoRepoAccessConfigured: FC = () => {
    return (
        <div
            className={
                "inline-flex cursor-pointer flex-row items-center rounded-[20px] border-[0.5px] border-border-base bg-surface-orange/40 px-2 py-1"
            }
        >
            <Tooltip
                content={
                    <div>
                        <div>Source control is not configured for this runner.</div>
                        <div>
                            Please, configure a repository provider (GitHub, GitLab, Bitbucket or Azure DevOps) to
                            access your repo.
                        </div>
                    </div>
                }
            >
                <div className={"animate-pulse text-sm font-bold text-content-orange"}>
                    <div>No repo access</div>
                </div>
            </Tooltip>
        </div>
    );
};
